import { useMemo } from "react";
import { CartData } from "../types/cart.types";
import { Product, Package } from "../types/product.types";
import { StorageItem } from "../types/storageItem.types";
import { czk_eur_rate } from "../constants/prices";
import { formatCurrency } from "../utils/prices";
import {
    groupCartData,
    getCartPriceSummary,
    getCartPriceSummaryCurrency,
    getGroupedBlockCount,
    getSvozBlockCount,
    hasTransportForItems,
    CartPriceSummaryCurrency,
} from "../utils/cart";

type CartPriceSummaryOptions = {
    cartData: CartData | null | undefined;
    selectedCurrency: string;
    allProducts?: Product[];
    packages?: Package[];
    items?: StorageItem[];
};

type CartPriceSummaryResult = {
    groupedCart: ReturnType<typeof groupCartData>;
    blocksCount: number;
    transportEx: boolean;
    priceSummary: ReturnType<typeof getCartPriceSummary>;
    priceSummaryCurrency: CartPriceSummaryCurrency;
    indicatorText: string;
};

/**
 * Hook to calculate cart price summary with currency conversion.
 * Consolidates the repeated price calculation logic used across Navbar and Cart components.
 *
 * @param options - Cart data and product catalogs
 * @returns All calculated cart price values
 */
export function useCartPriceSummary({
    cartData,
    selectedCurrency,
    allProducts = [],
    packages = [],
    items = [],
}: CartPriceSummaryOptions): CartPriceSummaryResult {
    const groupedCart = useMemo(
        () => groupCartData(cartData ?? null),
        [cartData]
    );

    const blocksCount = useMemo(
        () => getGroupedBlockCount(groupedCart),
        [groupedCart]
    );

    const transportEx = useMemo(() => {
        if (!cartData) return false;
        if (!cartData.items) return false;

        return hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
    }, [cartData, items, allProducts, packages]);

    const svozBlockCount = useMemo(
        () => getSvozBlockCount(groupedCart, { sales: allProducts, packages, storage: items }),
        [groupedCart, allProducts, packages, items]
    );

    const priceSummary = useMemo(
        () => getCartPriceSummary(cartData, blocksCount, svozBlockCount),
        [cartData, blocksCount, svozBlockCount]
    );

    const priceSummaryCurrency = useMemo(
        () => getCartPriceSummaryCurrency(priceSummary, selectedCurrency, czk_eur_rate),
        [priceSummary, selectedCurrency]
    );

    const indicatorText = formatCurrency(priceSummaryCurrency.totalNoTax, selectedCurrency);

    return {
        groupedCart,
        blocksCount,
        transportEx,
        priceSummary,
        priceSummaryCurrency,
        indicatorText,
    };
}
